import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { PlayIcon, MapPinIcon, ClockIcon } from 'lucide-react';
import { SubscribeButton } from './SubscribeButton';
import { HeroImage } from './HeroImage';
import { channel, channelStats, seo } from '../data/channel';
import { formatAgo, videoUrl } from '../utils/video';
import type { Video } from '../types/video';
interface HeroProps {
  video: Video;
}
export function Hero({
  video
}: HeroProps) {
  const reduce = useReducedMotion();
  return <section id="top-of-page" aria-labelledby="hero-title" className="relative isolate flex min-h-[calc(92vh-4rem)] w-full overflow-hidden" data-id="element-28" data-mp="xbfmhg6avp8a">
      <HeroImage video={video} data-id="element-29" data-mp="ncgm0kvouzwd" />
      <div className="absolute inset-0 -z-10 bg-ink/55" aria-hidden="true" data-id="element-30" data-mp="nb6llp1d5fmk" />

      <div className="mx-auto flex w-full max-w-7xl flex-col justify-end px-5 pb-14 pt-16 md:px-8 md:pb-20 md:pt-20" data-id="element-31" data-mp="3d21sdxnwkknq">
        <motion.div initial={reduce ? false : {
        opacity: 0,
        y: 14
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: [0.23, 1, 0.32, 1]
      }} className="max-w-4xl" data-id="element-32" data-mp="381e2jfbeu9v3">
          <h1 className="text-sm font-semibold text-paper/80" data-id="element-33" data-mp="3o6u6n92nspce">
            {channel.name} — {seo.tagline.toLowerCase()}
          </h1>
          <p className="mt-2 text-sm font-semibold text-paper/80" data-id="element-34" data-mp="3e9607lvoxtee">
            <span className="text-ember" data-id="element-35" data-mp="3e7l8s54oaqkr">Останній випуск</span> · {formatAgo(video.publishedAt)}
          </p>
          <h2 id="hero-title" className="mt-4 font-display text-[1.75rem] font-semibold leading-[1.12] tracking-tight text-paper sm:text-4xl lg:text-5xl" data-id="element-36" data-mp="30fybq32wpe0x">
            {video.title}
          </h2>
          {video.description && <p className="mt-5 max-w-xl text-lg leading-relaxed text-paper/85" data-id="element-37" data-mp="2vjpzoyk0a0xz">{video.description}</p>}

          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-paper/75" data-id="element-38" data-mp="2hjyot6osd0o7">
            {video.place && <span className="inline-flex items-center gap-1.5" data-id="element-39" data-mp="s21iocxnug0i">
                <MapPinIcon className="h-4 w-4" aria-hidden="true" data-id="element-40" data-mp="s3cki77jpdoz" />
                {video.place}
                {video.country ? `, ${video.country}` : ''}
              </span>}
            <span className="inline-flex items-center gap-1.5" data-id="element-41" data-mp="icrn2n3lyf7n">
              <ClockIcon className="h-4 w-4" aria-hidden="true" data-id="element-42" data-mp="ddwx91783qe1" />
              {video.duration}
            </span>
          </div>

          <div className="mt-9 flex flex-wrap items-center gap-3" data-id="element-43" data-mp="1b0dte347zxy7">
            <a href={videoUrl(video)} target="_blank" rel="noopener noreferrer" className="inline-flex h-14 items-center gap-2.5 whitespace-nowrap rounded-full bg-paper px-7 text-base font-bold text-ink transition-[background-color,transform] duration-150 ease-out hover:bg-white active:scale-[0.97] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-paper" data-id="element-44" data-mp="161ja702cc7dk">
              <PlayIcon className="h-5 w-5 fill-current" aria-hidden="true" data-id="element-45" data-mp="1x9kuunds16hq" />
              Дивитись випуск
            </a>
            <SubscribeButton size="lg" label="Підписатися на канал" data-id="element-46" data-mp="21a4tjve00i5z" />
          </div>
        </motion.div>

        <dl className="mt-12 flex flex-wrap gap-x-10 gap-y-3 border-t border-paper/20 pt-6" data-id="element-47" data-mp="268y5vkhju34k">
          {channelStats.map(stat => <div key={stat.label} className="flex items-baseline gap-2" data-id="element-48" data-mp="2b20tzd3k4kdd">
              <dt className="sr-only" data-id="element-49" data-mp="2fz0yhjkrdf2d">{stat.label}</dt>
              <dd className="font-display text-xl font-semibold text-paper" data-id="element-50" data-mp="2kyna5okxun92">{stat.value}</dd>
              <span className="text-sm text-paper/70" aria-hidden="true" data-id="element-51" data-mp="2p0h6xls0j73y">
                {stat.label}
              </span>
            </div>)}
        </dl>
      </div>
    </section>;
}